/**
 * Datart
 *
 * Copyright 2021
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

import { ChartConfig } from 'app/types/ChartConfig';

const config: ChartConfig = {
  datas: [
    {
      label: 'dimension',
      key: 'dimension',
      required: true,
      type: 'group',
      limit: [1, 1],
    },
    {
      label: 'metrics',
      key: 'metrics',
      required: true,
      rows: [],
      type: 'aggregate',
      limit: [1, 999],
    },
    {
      label: 'maxValue',
      key: 'maxValue',
      type: 'aggregate',
      limit: [0, 1],
    },
    {
      label: 'filter',
      key: 'filter',
      type: 'filter',
      allowSameField: true,
    },
    {
      label: 'info',
      key: 'info',
      type: 'info',
    },
  ],
  styles: [
    {
      label: 'radar.title',
      key: 'radar',
      comType: 'group',
      rows: [
        {
          label: 'radar.shape',
          key: 'shape',
          default: 'polygon',
          comType: 'select',
          options: {
            translateItemLabel: true,
            items: [
              { label: '@global@.radar.shapeType.polygon', value: 'polygon' },
              { label: '@global@.radar.shapeType.circle', value: 'circle' },
            ],
          },
        },
        {
          label: 'radar.splitNumber',
          key: 'splitNumber',
          default: 5,
          comType: 'inputNumber',
          options: {
            min: 1,
            step: 1,
          },
        },
        {
          label: 'radar.max',
          key: 'max',
          comType: 'inputNumber',
        },
        {
          label: 'radar.showSplitArea',
          key: 'showSplitArea',
          default: true,
          comType: 'checkbox',
        },
      ],
    },
    {
      label: 'graph.title',
      key: 'graph',
      comType: 'group',
      rows: [
        {
          label: 'graph.lineWidth',
          key: 'lineWidth',
          default: 2,
          comType: 'inputNumber',
          options: {
            min: 0,
            step: 1,
          },
        },
        {
          label: 'graph.showSymbol',
          key: 'showSymbol',
          default: true,
          comType: 'checkbox',
        },
        {
          label: 'graph.showAreaBackground',
          key: 'showAreaBackground',
          default: false,
          comType: 'checkbox',
        },
      ],
    },
    {
      label: 'label.title',
      key: 'label',
      comType: 'group',
      rows: [
        {
          label: 'label.showLabel',
          key: 'showLabel',
          default: false,
          comType: 'checkbox',
        },
        {
          label: 'viz.palette.style.font',
          key: 'font',
          comType: 'font',
          default: {
            fontFamily: 'PingFang SC',
            fontSize: '12',
            fontWeight: 'normal',
            fontStyle: 'normal',
            color: 'black',
          },
        },
      ],
    },
    {
      label: 'legend.title',
      key: 'legend',
      comType: 'group',
      rows: [
        {
          label: 'legend.showLegend',
          key: 'showLegend',
          default: true,
          comType: 'checkbox',
        },
        {
          label: 'legend.type',
          key: 'type',
          comType: 'legendType',
          default: 'scroll',
        },
        {
          label: 'legend.selectAll',
          key: 'selectAll',
          default: true,
          comType: 'checkbox',
        },
        {
          label: 'legend.position',
          key: 'position',
          comType: 'legendPosition',
          default: 'right',
        },
        {
          label: 'viz.palette.style.font',
          key: 'font',
          comType: 'font',
          default: {
            fontFamily: 'PingFang SC',
            fontSize: '12',
            fontWeight: 'normal',
            fontStyle: 'normal',
            color: 'black',
          },
        },
      ],
    },
  ],
  settings: [
    {
      label: 'viz.palette.setting.paging.title',
      key: 'paging',
      comType: 'group',
      rows: [
        {
          label: 'viz.palette.setting.paging.pageSize',
          key: 'pageSize',
          default: 1000,
          comType: 'inputNumber',
          options: {
            needRefresh: true,
            step: 1,
            min: 0,
          },
        },
      ],
    },
  ],
  i18ns: [
    {
      lang: 'zh-CN',
      translation: {
        radar: {
          title: '雷达坐标',
          shape: '雷达形状',
          splitNumber: '刻度分割段数',
          max: '最大刻度值',
          showSplitArea: '分隔区域',
          shapeType: {
            polygon: '多边形',
            circle: '圆形',
          },
        },
        graph: {
          title: '图形',
          lineWidth: '线条宽度',
          showSymbol: '显示拐点',
          showAreaBackground: '区域背景',
        },
        label: {
          title: '标签',
          showLabel: '显示标签',
        },
        legend: {
          title: '图例',
          showLegend: '显示图例',
          type: '图例类型',
          selectAll: '图例全选',
          position: '图例位置',
        },
      },
    },
    {
      lang: 'en',
      translation: {
        radar: {
          title: 'Radar Coordinate',
          shape: 'Shape',
          splitNumber: 'Split Number',
          max: 'Max Scale Value',
          showSplitArea: 'Split Area',
          shapeType: {
            polygon: 'Polygon',
            circle: 'Circle',
          },
        },
        graph: {
          title: 'Graph',
          lineWidth: 'Line Width',
          showSymbol: 'Show Symbol',
          showAreaBackground: 'Area Background',
        },
        label: {
          title: 'Label',
          showLabel: 'Show Label',
        },
        legend: {
          title: 'Legend',
          showLegend: 'Show Legend',
          type: 'Type',
          selectAll: 'Select All',
          position: 'Position',
        },
      },
    },
  ],
};

export default config;
